<template>

    <main class="ministries-page">

        <section class="ministries-page__hero">

            <div class="container">

                <span class="ministries-page__eyebrow">
                    {{ 'ministries_page' | staticContentAlt }}
                </span>

                <h1>
                    {{ 'ministries_page' | staticTitle }}
                </h1>

                <p>
                    {{ 'ministries_page' | staticContent }}
                </p>

            </div>

        </section>

        <section class="ministries-page__content">

            <div class="container">

                <article
                    v-for="(ministry, index) in ministries"
                    :id="ministry.id"
                    :key="ministry.hash"
                    class="ministry-detail"
                    :class="{ 'ministry-detail--reverse': index % 2 !== 0 }"
                >

                    <div class="ministry-detail__image">

                        <img
                            :src="ministry.hash | staticImg"
                            :alt="ministry.hash | staticTitle"
                        >

                    </div>

                    <div class="ministry-detail__content">

                        <span class="ministry-detail__number">
                            {{ formatNumber(index + 1) }}
                        </span>

                        <h2>
                            {{ ministry.hash | staticTitle }}
                        </h2>

                        <p class="ministry-detail__summary">
                            {{ ministry.hash | staticContent }}
                        </p>

                        <div class="ministry-detail__body">

                            <vue-marked>
                                {{ ministry.detailHash | staticContent }}
                            </vue-marked>

                        </div>

                        <div class="ministry-detail__meta">

                            <div>

                                <span>
                                    WHO IT'S FOR
                                </span>

                                <strong>
                                    {{ getAudience(ministry.detailHash) }}
                                </strong>

                            </div>

                            <div>

                                <span>
                                    AGES
                                </span>

                                <strong>
                                    {{ getAges(ministry.detailHash) }}
                                </strong>

                            </div>

                        </div>

                    </div>

                </article>

            </div>

        </section>

    </main>

</template>

<script>
export default {
    name: 'Portfolio',

    data() {
        return {
            ministries: [
                {
                    id: 'worship',
                    hash: 'ministry_worship',
                    detailHash: 'ministry_worship_detail'
                },
                {
                    id: 'hospital-ministry',
                    hash: 'ministry_hospital',
                    detailHash: 'ministry_hospital_detail'
                },
                {
                    id: 'teaching',
                    hash: 'ministry_teaching',
                    detailHash: 'ministry_teaching_detail'
                },
                {
                    id: 'sewing-ministry',
                    hash: 'ministry_sewing',
                    detailHash: 'ministry_sewing_detail'
                },
                {
                    id: 'community',
                    hash: 'ministry_community',
                    detailHash: 'ministry_community_detail'
                },
                {
                    id: 'outreach',
                    hash: 'ministry_outreach',
                    detailHash: 'ministry_outreach_detail'
                }
            ]
        };
    },

    methods: {
        formatNumber(number) {
            return number < 10 ? `0${number}` : number;
        },

        getAlt(hash) {
            return this.$options.filters.staticContentAlt(hash) || '';
        },

        getAudience(hash) {
            const values = this.getAlt(hash).split('|');

            return values[0] || '';
        },

        getAges(hash) {
            const values = this.getAlt(hash).split('|');

            return values[1] || '';
        }
    }
};
</script>

<style lang="scss" scoped>
.ministries-page {
    background: var(--background);

    &__hero {
        padding: 160px 20px 90px;

        background: #262626;

        text-align: center;

        .container {
            width: 100%;
            max-width: 1100px;

            margin: 0 auto;
        }

        h1 {
            max-width: 900px;

            margin: 12px auto 22px;

            font-family: 'Cormorant Garamond', serif;

            font-size: clamp(3.2rem, 8vw, 5.5rem);
            font-weight: 600;

            line-height: .95;

            color: #fff;
        }

        p {
            max-width: 680px;

            margin: 0 auto;

            color: rgba(255, 255, 255, .7);

            font-size: .95rem;

            line-height: 1.8;
        }
    }

    &__eyebrow {
        display: inline-block;

        color: var(--primary);

        font-size: .75rem;
        font-weight: 600;

        letter-spacing: 3px;

        text-transform: uppercase;
    }

    &__content {
        padding: 70px 20px 100px;

        .container {
            width: 100%;
            max-width: 1200px;

            margin: 0 auto;
        }
    }
}

.ministry-detail {
    display: flex;
    flex-direction: column;

    margin-bottom: 90px;

    scroll-margin-top: 110px;

    &:last-child {
        margin-bottom: 0;
    }

    &__image {
        width: 100%;

        overflow: hidden;

        background: #f3f3f3;

        img {
            display: block;

            width: 100%;

            aspect-ratio: 4 / 3;

            object-fit: cover;

            transition: transform .6s ease;
        }
    }

    &__content {
        padding: 35px 0 0;
    }

    &__number {
        display: inline-block;

        margin-bottom: 15px;

        color: var(--primary);

        font-size: .75rem;
        font-weight: 700;

        letter-spacing: 2px;
    }

    h2 {
        margin: 0 0 18px;

        font-family: 'Cormorant Garamond', serif;

        font-size: clamp(2.4rem, 7vw, 3.7rem);
        font-weight: 600;

        line-height: 1;

        color: var(--text);
    }

    &__summary {
        max-width: 540px;

        margin-bottom: 24px;

        color: var(--text);

        font-size: 1.05rem;
        font-weight: 500;

        line-height: 1.7;
    }

    &__body {
        max-width: 580px;

        ::v-deep p {
            margin: 0 0 18px;

            color: #666;

            font-size: .95rem;

            line-height: 1.85;
        }

        ::v-deep ul {
            margin: 18px 0;

            padding-left: 20px;

            color: #666;

            font-size: .95rem;

            line-height: 1.8;
        }

        ::v-deep strong {
            color: var(--text);
        }
    }

    &__meta {
        display: grid;

        grid-template-columns: 1fr;

        gap: 16px;

        margin-top: 30px;
        padding: 22px 0;

        border-top: 1px solid #e5e5e5;
        border-bottom: 1px solid #e5e5e5;

        div {
            display: flex;
            flex-direction: column;

            gap: 5px;
        }

        span {
            color: var(--primary);

            font-size: .68rem;
            font-weight: 700;

            letter-spacing: 1.5px;
        }

        strong {
            color: var(--text);

            font-size: .9rem;
            font-weight: 500;
        }
    }

    &:hover {

        .ministry-detail__image img {
            transform: scale(1.025);
        }
    }
}

@media (min-width: 600px) {
    .ministry-detail {

        &__meta {
            grid-template-columns: repeat(2, 1fr);
        }
    }
}

@media (min-width: 900px) {
    .ministries-page {

        &__hero {
            padding: 185px 20px 115px;

            p {
                font-size: 1rem;
            }
        }

        &__content {
            padding: 110px 30px 140px;
        }
    }

    .ministry-detail {
        display: grid;

        grid-template-columns: 1fr 1fr;

        align-items: center;

        gap: 75px;

        margin-bottom: 140px;

        &--reverse {

            .ministry-detail__image {
                order: 2;
            }

            .ministry-detail__content {
                order: 1;
            }
        }

        &__image {

            img {
                aspect-ratio: 4 / 5;
            }
        }

        &__content {
            padding: 0;
        }
    }
}

@media (min-width: 1200px) {
    .ministry-detail {
        gap: 110px;

        margin-bottom: 170px;

        &__content {
            padding: 0 20px;
        }
    }
}
</style>